import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useWorkspaceText } from '../interface-copy';
import { useEffect, useState, type ReactNode } from 'react';
import { useCompany } from '@/company/context';
import { useAgentFeatures } from '../agent-features';
import { useWorkspace } from '../context';
import { EmptyState, ErrorState, LoadingState } from '../primitives';
import { errorStatus, knowledgePagesApi, pagesErrorMessage } from './api';
import { pageTarget } from './format';
import { KnowledgePageView } from './page-view';
import { useKnowledgeTree } from './use-wiki';

// The company knowledge wiki: a page tree, pages with inline suggestions,
// history, the editor and the review list.
export { PageProposalChatCard } from './chat-card';
export { suggestionCanvas, suggestionPointer } from './suggestion-link';
export { KnowledgePageView } from './page-view';
export { knowledgePagesApi } from './api';
export { loadKnowledgeEditor } from './editor/load';
export { useKnowledgeTree, usePendingProposals } from './use-wiki';
export { TreeNav } from './tree-nav';
export { ReviewList } from './review-list';
export { PageEditor } from './editor-panel';

/**
 * A deep link, citation or Finder hit can arrive before the rollout flag is
 * known, or while it is off. Poneva serves no page reads then either, so the
 * canvas states that plainly and asks for nothing; it is not an error.
 */
function PagesFeature({ label, children }: { label: string; children: ReactNode }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const features = useAgentFeatures();
  if (!features) return <LoadingState label={label} />;
  if (!features.knowledgePages)
    return (
      <div className="ws-canvas-body" data-feature="knowledge-pages-off">
        <EmptyState
          icon="knowledge"
          title={uiText('Knowledge pages are not enabled in this workspace.')}
          description={uiText('Ask your Poneva contact to switch them on for your company.')}
        />
      </div>
    );
  return <>{children}</>;
}

interface CanvasPageTarget {
  id: string;
  full?: boolean;
  section?: string;
  suggestion?: string;
  revision?: string;
}

function CanvasPage({ target }: { target: CanvasPageTarget }) {
  const locale = useChatInterfaceLocale();
  const ws = useWorkspace();
  const tree = useKnowledgeTree();
  return (
    <KnowledgePageView
      pageId={target.id}
      layout={target.full ? 'full' : 'docked'}
      focus={{
        suggestion: target.suggestion,
        section: target.section,
        revision: target.revision,
      }}
      tree={tree}
      navigation={{
        openPage: (id, focus, options) =>
          ws.openCanvas(pageTarget({ id, full: target.full, ...focus }), options),
      }}
    />
  );
}

/** A knowledge page in the canvas: docked beside a chat, or full width. */
export function KnowledgePageCanvas({ target }: { target: CanvasPageTarget }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  // The shell keeps one canvas mounted across targets; another page (or the
  // same id in another company) starts clean, with no draft or focus carried over.
  const companyId = useCompany().data?.company?.id ?? '';
  return (
    <PagesFeature label={uiText('Loading page…')}>
      <CanvasPage key={`${companyId}\0${target.id}`} target={target} />
    </PagesFeature>
  );
}

/** The knowledge canvas without a page: the company's root page. */
export function KnowledgeRootCanvas({ full }: { full: boolean }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  return (
    <PagesFeature label={uiText('Loading company knowledge…')}>
      <RootPage full={full} />
    </PagesFeature>
  );
}

function RootPage({ full }: { full: boolean }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const tree = useKnowledgeTree();
  if (tree.error && !tree.tree)
    return (
      <div className="ws-canvas-body">
        <ErrorState message={tree.error} onRetry={tree.retry} />
      </div>
    );
  if (!tree.tree) return <LoadingState label={uiText('Loading company knowledge…')} />;
  return <KnowledgePageCanvas target={{ id: tree.tree.rootId, full }} />;
}

/**
 * A link to one suggestion (`canvas=page-proposal`: Inbox items, older chats)
 * opens the page it belongs to with that suggestion in focus: an edit's page,
 * a suggested new page's parent (the root when it has none).
 */
export function PageProposalCanvas({ id, full }: { id: string; full: boolean }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  return (
    <PagesFeature label={uiText('Loading suggestion…')}>
      <ProposalRedirect id={id} full={full} />
    </PagesFeature>
  );
}

function ProposalRedirect({ id, full }: { id: string; full: boolean }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const companyId = useCompany().data?.company?.id ?? '';
  const [error, setError] = useState<{ message: string; missing: boolean } | null>(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!companyId) return;
    let active = true;
    setError(null);
    void knowledgePagesApi
      .proposal(id)
      .then(async (proposal) => {
        // A pending new page shows on its parent; a decided one is its own page.
        const direct =
          proposal.kind === 'create_page' && proposal.status === 'pending'
            ? proposal.parentId
            : (proposal.pageId ?? proposal.parentId);
        return direct ?? (await knowledgePagesApi.tree()).rootId;
      })
      .then(
        (pageId) => {
          if (active)
            ws.openCanvas(pageTarget({ id: pageId, suggestion: id, full }), { replace: true });
        },
        (failure) => {
          if (!active) return;
          const status = errorStatus(failure);
          setError({
            message: pagesErrorMessage(
              failure,
              uiText('We could not open this suggestion.'),
              uiText.locale,
            ),
            missing: status === 404 || status === 403,
          });
        },
      );
    return () => {
      active = false;
    };
  }, [id, companyId, retry]);
  if (error?.missing)
    return (
      <div className="ws-canvas-body">
        <EmptyState
          icon="knowledge"
          title={uiText('This suggestion is not available.')}
          description={uiText('It may have been decided, or it is not one you can see.')}
        >
          <button type="button" className="ws-button" onClick={() => ws.navigate('knowledge')}>
            {uiText('Open Knowledge')}
          </button>
        </EmptyState>
      </div>
    );
  if (error)
    return (
      <div className="ws-canvas-body">
        <ErrorState message={error.message} onRetry={() => setRetry((value) => value + 1)} />
      </div>
    );
  return <LoadingState label={uiText('Opening the suggestion…')} />;
}
